import type { Hero } from "./Hero";

export interface InventorySlot {
  id: string;
  count: number;
}

/** Shared state that survives between scenes: the party, its stuff, its flags. */
export class Party {
  members: Hero[] = [];
  gold = 0;
  items: InventorySlot[] = [];
  /** Story flags — chests opened, quests done, NPCs talked to. */
  flags = new Set<string>();
  /** Total steps walked; used for encounter pacing and idle chatter. */
  steps = 0;

  constructor(members: Hero[] = [], gold = 0) {
    this.members = members;
    this.gold = gold;
  }

  get leader(): Hero {
    return this.members[0];
  }

  get living(): Hero[] {
    return this.members.filter((m) => m.alive);
  }

  get wiped(): boolean {
    return this.members.every((m) => !m.alive);
  }

  addItem(id: string, count = 1): void {
    const slot = this.items.find((s) => s.id === id);
    if (slot) slot.count += count;
    else this.items.push({ id, count });
  }

  removeItem(id: string, count = 1): boolean {
    const slot = this.items.find((s) => s.id === id);
    if (!slot || slot.count < count) return false;
    slot.count -= count;
    if (slot.count <= 0) this.items = this.items.filter((s) => s !== slot);
    return true;
  }

  countItem(id: string): number {
    return this.items.find((s) => s.id === id)?.count ?? 0;
  }

  hasItem(id: string): boolean {
    return this.countItem(id) > 0;
  }

  setFlag(flag: string): void {
    this.flags.add(flag);
  }

  hasFlag(flag: string): boolean {
    return this.flags.has(flag);
  }

  fullHeal(): void {
    for (const member of this.members) member.fullHeal();
  }

  /** Revives fallen members at 1 HP — what a temple or a defeat does. */
  reviveAll(): void {
    for (const member of this.members) {
      if (!member.alive) member.hp = 1;
    }
  }
}
